import { Button, Flex, Select, Text } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { CONTROL_HISTORY_DUR, EXCEL_LINES, NumOfBsd, NumOfGroup, PAGE_SIZE } from "CommonData";
import SRD_DataTable from "components/DataTable";
import Layout from "components/Layout";
import debounce from 'lodash/debounce';
import { getBsdList } from "pages/Admin/Device/apis/adminDeviceApis";
import { getGroupList } from "pages/Admin/Group/apis/adminGroupApis";
import { userState } from "pages/Login/atom/userState";
import { useState, useMemo, useEffect } from 'react'; // useCallback 추가
import { useQuery, useQueryClient } from 'react-query'; // useQuery 및 useQueryClient 추가
import { useRecoilValue } from "recoil";
import * as XLSX from 'xlsx';
import { getControlHistory } from "./apis/controlHistoryApis";

function ControlHistory() {
  const queryClient = useQueryClient(); // queryClient 초기화
  const [shouldFetch, setShouldFetch] = useState(false); // 데이터 가져오기 여부를 제어하는 상태 추가
  // const [isLoading, setIsLoading] = useState(false); // 로딩 상태 추가
  const [page, setPage] = useState(1);

  const user = useRecoilValue(userState);
  const level = Number(user?.fn_level);
  const adminLevel = [0].includes(level);
  const loginGroupId = adminLevel ? null : user?.fn_group_id;
  const [group, setGroup] = useState(loginGroupId);
  const [bsd, setBsd] = useState();
  
  const [range, setRange] = useState({
    start: new Date(new Date().getTime() - CONTROL_HISTORY_DUR * 24 * 60 * 60 * 1000),
    end: new Date(),
  });

  const dateToString = (date) => {
    return date.toISOString().slice(0, 10).replace(/-/g, "");
  };

  const { data: groupList } = useQuery(
    'groupList',
    () => getGroupList(NumOfGroup, 1).then(groupList => 
      groupList.map(item => ({
        value: item.group_id,
        label: item.group_nm,
      }))
    ), 
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  const { data: bsdList } = useQuery(
    ['bsdList', group],
    () => getBsdList(NumOfBsd, 1, group).then(bsdList =>
      bsdList
        .filter(item => item.group_id === group)
        .map(item => ({
          value: item.bsd_id,
          label: item.bsd_nm,
        }))
    ),
    {
      enabled: !!group,
      staleTime: 5 * 60 * 1000,
    }
  );

  const { data: controlHistoryList, refetch } = useQuery(
    ['controlHistory', page, range.start, range.end, group, bsd],
    async () => {
      // setIsLoading(true); // 로딩 시작
      try {
        const result = await getControlHistory(
          PAGE_SIZE,
          page,
          dateToString(range.start),
          dateToString(range.end),
          group,
          bsd
        );
        return result; // result가 undefined일 수 있음
      } catch (error) {
        console.error("이력을 가져오는 중 오류가 발생했습니다:", error);
        return []; // 오류 발생 시 빈 배열 반환
      } finally {
        // setIsLoading(false); // 로딩 종료
      }
    },
    {
      enabled: shouldFetch || page !== 1, // shouldFetch가 true이거나 page가 1이 아닐 때 실행
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      onSuccess: (data) => {
        if (!data || !Array.isArray(data) || data.length === 0) { // data가 undefined이거나 배열이 아니거나 길이가 0일 때
          alert("기록된 이력사항이 없습니다."); // 데이터가 없을 때 alert 발생
        }
        setShouldFetch(false); // 데이터를 성공적으로 가져온 후 다시 false로 설정
      },
    }
  );

  const debouncedSearch = useMemo(
    () =>
      debounce(() => {
        setPage(1);
        queryClient.invalidateQueries('controlHistory');
      }, 300),
    [queryClient]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearch = () => {  //===> [검색] 버튼 눌렀을 때
    // console.log("range : ", range);
    setPage(1); // 페이지를 1로 초기화
    setShouldFetch(true); // 데이터 가져오기 시작
    refetch(); // refetch 호출
    // queryClient.invalidateQueries('controlHistory'); // controlHistory 쿼리 무효화
  };

  const handleBsdChange = (value) => {
    setBsd(value); // bsd 상태 업데이트
    // 검색 버튼을 눌러야만 데이터를 가져오도록 설정
    setShouldFetch(false); // 다음 검색위해
  };

  const columns = [
    {
      accessor: "index",
      title: "번호",
      textAlignment: "center",
      width: 30,
      render: (record) => record?.rnum,
    },
    {
      accessor: "ctrl_dt",
      title: "제어 일시",
    },
    {
      accessor: "bsd_nm",
      title: "분사 장치명",
    },
    {
      accessor: "chnnl_nm",
      title: "제어 항목",
    },
    {
      accessor: "chnnl_ctrl_value",
      title: "제어 값",
      render: (record) => {
        const chnnlCtrlValue = record.chnnl_ctrl_value || ""; 
        const rmValue = record.rm !== undefined && record.rm !== null ? record.rm : "";
        return `${chnnlCtrlValue ? `${chnnlCtrlValue} ` : ""} ${rmValue !== "" ? `(${rmValue})` : ""}`;
      }		// '{chnnlCtrlValue},' delete
    },   
    {
      accessor: "user_id",
      title: "사용자 ID",
    },
  ];

  const SearchBar = () => {
    const handleStartChange = (date) => {
      const today = new Date();
      today.setHours(23, 59, 59, 999); // 오늘 자정까지의 이력 수집울 위해서
    
      setRange((prevState) => {
        const newEnd = new Date(date);
        // start는 today까지 선택 가능
        const newStart = date <= today ? date : today;

        newEnd.setDate(newStart.getDate() + CONTROL_HISTORY_DUR); // date에 30일 추가
        // end는 today보다 항상 같거나 작도록 구해짐
        const finalEnd = newEnd > today ? today : newEnd;

        return { 
          ...prevState, 
          start: newStart, // start는 today까지 선택 가능
          end: finalEnd // end가 유효하면 기존 end 유지
        };
      });
    };

    const handleEndChange = (date) => {
      setRange(prev => ({ ...prev, end: date }));
      debouncedSearch();
    };

    return (
      <Flex gap={15} mb={10} align="center">
        <Select 
          disabled={!adminLevel}
          data={groupList ?? []} 
          value={group} 
          placeholder="그룹 선택" 
          onChange={setGroup} 
        />
        <Select 
          data={bsdList ?? []} 
          value={bsd} 
          placeholder="분사 장치 선택" 
          onChange={handleBsdChange} // bsd 변경 시 핸들러 호출
        />
        <Text fw={500}>시작일</Text>
        <DateInput
          value={range.start}
          onChange={handleStartChange}
          valueFormat="YYYY-MM-DD"
          inputProps={{
            rightSection: <i className="fas fa-calendar-alt"></i>,
          }}
          maxDate={new Date()}
        />
        <Text fw={500}>종료일</Text>
        <DateInput
          value={range.end}
          onChange={handleEndChange}
          valueFormat="YYYY-MM-DD"
          inputProps={{
            rightSection: <i className="fas fa-calendar-alt"></i>,
          }}
          minDate={range.start}
          maxDate={new Date(Math.min(range.start.getTime() + CONTROL_HISTORY_DUR * 24 * 60 * 60 * 1000, new Date().getTime()))}
        />
        <Button onClick={handleSearch}>검색</Button>
        <Button onClick={downloadExcelFile}>Excel 다운로드</Button>
      </Flex>
    );
  };

  const downloadExcelFile = async () => {
    try {
      const excelHistoryList = await getControlHistory(
        EXCEL_LINES,
        1,
        dateToString(range.start),
        dateToString(range.end),
        group,
        bsd
      );
  
      if (excelHistoryList && excelHistoryList.length > 0) {
          const dataSet = excelHistoryList.map((item) => ({
          번호: item.rnum,
          제어_일시: item.ctrl_dt,
          분사_장치명: item.bsd_nm,
          제어_항목: item.chnnl_nm,
          제어_값: item.chnnl_ctrl_value,
          비_고: item.rm,
          사용자_ID: item.user_id,
        }));
  
        const xls = XLSX.utils.json_to_sheet(dataSet);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, xls, "Control History");

        /*
        const filteredData = dataSet.filter((item) => {
          const ctrlDate = new Date(item.제어_일시);
          return ctrlDate >= range.start && ctrlDate <= range.end;
        });
        */

        const currentDate = new Date();
        const todayFormattedDate = currentDate.toISOString().slice(0, 10).replace(/-/g, "");
        const fileName = `control_history_${todayFormattedDate}.xlsx`;
  
        XLSX.writeFile(wb, fileName);
      } else {
        alert("엑셀파일로 저장할 이력사항이 없습니다.");
      }
    } catch (error) {
      console.error("Error fetching control history:", error);
      alert("엑셀 다운로드 중 오류가 발생했습니다.");
    }
  };
  

  return (
    <Layout title="제어 이력">
      <SRD_DataTable
        searchBar={
          <SearchBar
          data={controlHistoryList ?? []}
          columns={columns}
          page={page}
          setPage={setPage}
          />
        }
        data={controlHistoryList ?? []} // 로딩 중일 때 빈 배열 전달
        columns={columns}
        page={page}
        setPage={setPage}
        idAccessor="rnum"
      />
    </Layout>
  );
}

export default ControlHistory;
